'use client';
import React from 'react';
import AppLayout from '@/components/AppLayout';
import { Shield } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <AppLayout activeRoute="/privacy-policy">
      <div className="max-w-3xl mx-auto fade-in">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
            <Shield size={20} className="text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Privacy Policy</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Effective Date: 21 May 2026</p>
          </div>
        </div>

        <div className="card-base p-8 space-y-8 text-sm text-muted-foreground leading-relaxed">
          <p className="text-foreground/80">
            Welcome to PropDataX (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our platform, or interact with our services.
          </p>
          <p className="text-foreground/80">
            By accessing or using PropDataX, you agree to the terms outlined in this Privacy Policy.
          </p>

          <Section title="1. Information We Collect">
            <p>We may collect the following types of information:</p>
            <SubSection title="Personal Information">
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>Full name</li>
                <li>Email address</li>
                <li>Mobile number</li>
                <li>Company name</li>
                <li>Billing information</li>
                <li>Login credentials</li>
              </ul>
            </SubSection>
            <SubSection title="Technical Information">
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Operating system</li>
                <li>Website usage data</li>
                <li>Cookies and analytics data</li>
              </ul>
            </SubSection>
            <SubSection title="User Submitted Data">
              <p className="mt-2">Any information uploaded, submitted, or shared by users while using the platform.</p>
            </SubSection>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use collected information to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Provide and improve our services</li>
              <li>Process payments and subscriptions</li>
              <li>Respond to customer support requests</li>
              <li>Send service updates and notifications</li>
              <li>Improve platform performance and security</li>
              <li>Prevent fraud and unauthorized access</li>
              <li>Comply with legal obligations</li>
            </ul>
          </Section>

          <Section title="3. Payment Information">
            <p>
              Payments made on PropDataX are securely processed through third-party payment gateway providers. We do not store complete debit card, credit card, UPI PIN, or banking credentials on our servers.
            </p>
          </Section>

          <Section title="4. Cookies and Tracking Technologies">
            <p>We may use cookies and similar technologies to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Improve user experience</li>
              <li>Analyze website traffic</li>
              <li>Remember user preferences</li>
              <li>Enhance platform functionality</li>
            </ul>
            <p className="mt-3">Users may disable cookies through browser settings.</p>
          </Section>

          <Section title="5. Data Sharing and Disclosure">
            <p>We do not sell personal information.</p>
            <p className="mt-3">We may share information with:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Payment gateway providers</li>
              <li>Hosting and infrastructure providers</li>
              <li>Analytics providers</li>
              <li>Legal or regulatory authorities when required by law</li>
            </ul>
          </Section>

          <Section title="6. Data Security">
            <p>
              We implement reasonable technical and organizational measures to protect user data from unauthorized access, misuse, or disclosure.
            </p>
            <p className="mt-3">However, no method of transmission over the internet is completely secure.</p>
          </Section>

          <Section title="7. Data Retention">
            <p>We retain user information for as long as necessary to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Provide services</li>
              <li>Resolve disputes</li>
              <li>Comply with legal obligations</li>
              <li>Enforce agreements</li>
            </ul>
          </Section>

          <Section title="8. User Rights">
            <p>Users may request:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Access to personal data</li>
              <li>Correction of inaccurate information</li>
              <li>Deletion of account and associated data</li>
              <li>Withdrawal of consent where applicable</li>
            </ul>
            <p className="mt-3">Requests may be sent to the contact details below.</p>
          </Section>

          <Section title="9. Third-Party Links">
            <p>
              Our platform may contain links to third-party websites. We are not responsible for the privacy practices or content of such websites.
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Updated versions will be posted on this page with revised effective dates.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>For questions regarding this Privacy Policy, please contact:</p>
            <div className="mt-3 p-4 bg-muted/40 rounded-lg border border-border space-y-1">
              <p className="font-semibold text-foreground">PropDataX</p>
              <p>Email: <a href="mailto:sales@propdatax.com" className="text-amber-400 hover:text-amber-300 transition-colors">sales@propdatax.com</a></p>
              <p>Website: <a href="https://propdatax.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 transition-colors">https://propdatax.com</a></p>
            </div>
          </Section>
        </div>
      </div>
    </AppLayout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold text-foreground border-b border-border pb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="text-sm font-medium text-foreground/90">{title}</h3>
      {children}
    </div>
  );
}
